/**
 * The pool's password policy, as a person needs to hear it before they pick one.
 *
 * The pool requires at least 8 characters, a lowercase letter and a number (describe-user-pool,
 * PasswordPolicy). The builder said nothing at all, and the published sites' sign-up page said
 * only "At least 8 characters" — so a password it called fine was refused with a message about
 * length. One sentence, the whole rule, wherever a password is chosen.
 */
export const PASSWORD_RULE = 'At least 8 characters, with a lowercase letter and a number';

/**
 * The same rule in parts, each checkable while it is typed (Sign up's checklist).
 *
 * Exactly infra/lib/stack.ts's passwordPolicy — minLength 8, requireLowercase, requireDigits,
 * and NOT uppercase or symbols. A part added here that the pool does not ask for would turn
 * people away whom the pool accepts; one left out would let the button send a password the
 * pool refuses. Lowercase and digits as Cognito counts them: the basic Latin a–z and 0–9.
 * Length in code points, not UTF-16 units, so an emoji is one character as it is to a person.
 */
export const PASSWORD_CHECKS: readonly { label: string; met: (password: string) => boolean }[] = [
  { label: '8+ characters', met: (p) => [...p].length >= 8 },
  { label: 'A lowercase letter', met: (p) => /[a-z]/.test(p) },
  { label: 'A number', met: (p) => /[0-9]/.test(p) },
];

/**
 * Cognito refuses a password that starts or ends with whitespace (its pattern is
 * ^[\S]+.*[\S]+$) with a message about regular expressions. Not part of the policy, so not a
 * line of the checklist; said only when it happens.
 */
export const hasEdgeSpace = (password: string): boolean => password.length > 0 && password !== password.trim();

/** Whether the pool will take it: every part met, and no space at either end. */
export const passwordAccepted = (password: string): boolean =>
  PASSWORD_CHECKS.every((c) => c.met(password)) && !hasEdgeSpace(password);
